import React from "react";
import { useHistory, withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router-dom";

import { useAuth } from "@api";
import { RobotsNoindex } from "@components/RobotsNoindex";
import { SESSION_CONTENTS } from "@constants";
import { recoverBrowserSecurityError } from "@utils";

interface State {
  hasError: boolean;
}

interface AppCrashFallbackProps {
  resetError?: () => void;
}
export const AppCrashFallback: React.FC<AppCrashFallbackProps> = ({
  resetError,
}) => {
  const { logout } = useAuth();
  const history = useHistory();
  return (
    <div style={{ padding: "24px" }}>
      <RobotsNoindex />
      <h1>Semgrep has crashed</h1>
      <p>
        Sorry about this! We've been notified and will get to work on fixing the
        root cause. Logging out might fix it. Wiping your editor session will
        lose your work from the editor, but it can fix some crashes that keep
        coming back when reloading the page.
      </p>
      <button onClick={logout}>Sign out</button>
      <button
        onClick={() => {
          recoverBrowserSecurityError(() =>
            localStorage.removeItem(SESSION_CONTENTS)
          );
          history.go(0);
        }}
      >
        Wipe editor session
      </button>
      {resetError && <button onClick={resetError}>Dismiss error</button>}
    </div>
  );
};

class ErrorBoundaryEl extends React.Component<
  React.PropsWithChildren<RouteComponentProps>,
  State
> {
  constructor(props: React.PropsWithChildren<RouteComponentProps>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ background: "#E9C982" }}>
          Oof, sorry, this editor has crashed, please{" "}
          <a href="https://github.com/returntocorp/semgrep/issues/new">
            report this as a bug
          </a>
          !{" "}
          <button
            onClick={() => {
              recoverBrowserSecurityError(() =>
                localStorage.removeItem(SESSION_CONTENTS)
              );
              this.props.history.go(0);
            }}
          >
            Reset session
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = withRouter(ErrorBoundaryEl);
